import { useQuery } from '@tanstack/react-query';
import { BarChart3, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { Card } from '../components/Card';

// T14 — org-level scoreboard: how busy the org is and who's on top, merged
// from the materialised division boards across every comp the org runs.
// Visible to every org member; a clan member can see where their clan sits.

export function OrgStatsPanel({ orgId }: { orgId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['orgStats', orgId],
    queryFn: () => api.orgStats(orgId),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <Card>
        <p className="flex items-center gap-2 text-xs text-iron-500"><Loader2 className="h-3 w-3 animate-spin" /> Loading org stats…</p>
      </Card>
    );
  }
  if (!data) return null;

  const counters: Array<{ label: string; value: number }> = [
    { label: 'Competitions', value: data.comps.total },
    { label: 'Live now', value: data.comps.byStatus.LIVE ?? 0 },
    { label: 'Clans', value: data.clanCount },
    { label: 'Members', value: data.memberCount },
    { label: 'Matches played', value: data.confirmedMatches },
  ];

  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-accent-500" />
        <h2 className="font-display text-xs font-black uppercase tracking-wider text-ink-secondary">
          Org Scoreboard
        </h2>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {counters.map(c => (
          <div key={c.label} className="border border-iron-800 bg-iron-950 px-3 py-2 text-center">
            <p className="font-display text-2xl font-black text-white">{c.value.toLocaleString()}</p>
            <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-iron-500">{c.label}</p>
          </div>
        ))}
      </div>

      {data.topClans.length > 0 && (
        <div className="mb-5">
          <p className="mb-2 font-display text-[10px] font-black uppercase tracking-[0.22em] text-iron-500">
            Top clans across all comps
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-iron-800 text-left font-mono text-[10px] uppercase tracking-wider text-iron-500">
                  <th className="pb-2 pr-3">#</th>
                  <th className="pb-2 pr-3">Clan</th>
                  <th className="pb-2 pr-3 text-center">Score</th>
                  <th className="pb-2 pr-3 text-center">W</th>
                  <th className="pb-2 pr-3 text-center">L</th>
                  <th className="pb-2 text-center">Comps</th>
                </tr>
              </thead>
              <tbody>
                {data.topClans.map(c => (
                  <tr key={c.clanName} className="border-b border-iron-900/60">
                    <td className="py-2 pr-3 text-iron-500">{c.rank}</td>
                    <td className="py-2 pr-3 font-display font-bold uppercase tracking-wide text-white">{c.clanName}</td>
                    <td className="py-2 pr-3 text-center font-mono text-forge-300">{c.totalScore.toLocaleString()}</td>
                    <td className="py-2 pr-3 text-center text-emerald-400">{c.wins}</td>
                    <td className="py-2 pr-3 text-center text-red-400">{c.losses}</td>
                    <td className="py-2 text-center text-iron-400">{c.compsSeen}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.perComp.length > 0 && (
        <div>
          <p className="mb-2 font-display text-[10px] font-black uppercase tracking-[0.22em] text-iron-500">
            Competitions
          </p>
          <div className="flex flex-wrap gap-2">
            {data.perComp.map(c => (
              <span key={c.id} className="inline-flex items-center gap-2 border border-iron-800 bg-iron-950 px-2.5 py-1.5 font-mono text-[10px] text-iron-300">
                <span className="font-display font-bold uppercase tracking-wide text-white">{c.name}</span>
                <span className="uppercase text-iron-500">{c.status}</span>
                <span className="text-iron-500">·</span>
                <span>{c.matchCount} match{c.matchCount !== 1 ? 'es' : ''}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
